import React from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, ChevronRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";

export const Events = () => {
  const { content } = useContent();
  const { t, L } = useLang();
  const events = content?.events || [];
  const contact = content?.contact || {};
  const waNumber = (contact.whatsapp || "").replace(/[^0-9]/g, "");

  const bookEvent = (ev) => {
    const msg = encodeURIComponent(`Ciao Salty Folks! Vorrei prenotare: ${L(ev.name)} (${L(ev.date)} ${ev.time || ""})`);
    window.open(`https://wa.me/${waNumber}?text=${msg}`, "_blank");
  };

  return (
    <section id="events" className="bg-sand-200 py-20 sm:py-28" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <div className="max-w-2xl mb-12">
          <p className="eyebrow text-coral mb-4">{t("events.eyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-ocean leading-[1.05]">
            {t("events.title")}
          </h2>
          <p className="text-ocean/60 mt-4 text-lg">{t("events.subtitle")}</p>
        </div>

        {events.length === 0 ? (
          <p className="text-ocean/50">{t("events.empty")}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev, i) => (
              <motion.div
                key={ev.id || i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`event-card-${ev.id || i}`}
                className="group bg-white rounded-2xl overflow-hidden border border-ocean/10 flex flex-col hover:shadow-xl transition-all"
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={ev.image} alt={L(ev.name)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ocean-900/50 to-transparent" />
                  <h3 className="absolute bottom-4 left-5 right-5 font-heading text-2xl font-extrabold text-sand-100 uppercase leading-tight">
                    {L(ev.name)}
                  </h3>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-ocean/80">
                      <Calendar className="w-4 h-4 text-coral shrink-0" />
                      <span className="font-mono text-[0.6rem] uppercase tracking-wider text-ocean/40 w-16">{t("events.date")}</span>
                      <span className="font-semibold">{L(ev.date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-ocean/80">
                      <Clock className="w-4 h-4 text-coral shrink-0" />
                      <span className="font-mono text-[0.6rem] uppercase tracking-wider text-ocean/40 w-16">{t("events.time")}</span>
                      <span className="font-semibold">{ev.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-ocean/80">
                      <MapPin className="w-4 h-4 text-coral shrink-0" />
                      <span className="font-mono text-[0.6rem] uppercase tracking-wider text-ocean/40 w-16">{t("events.place")}</span>
                      <span className="font-semibold">{L(ev.place)}</span>
                    </div>
                  </div>
                  <p className="text-ocean/60 text-sm mb-6 flex-1">{L(ev.description)}</p>
                  <button
                    onClick={() => bookEvent(ev)}
                    data-testid={`event-book-${ev.id || i}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral/90 text-white font-heading font-bold px-5 py-3 rounded-full transition-all hover:scale-[1.02]"
                  >
                    {t("events.book")} <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
